import type { AgentData } from "@/services/agent-list";
import { isAgentAvailableForUser } from "@/utils/agent-availability";

export type AgentEntryDestination =
  /** /select-system/：无可用时展示联系管理员弹窗；有可用时展示选择弹窗 */
  | { type: "select-system"; customDomainDisabledCode?: CustomDomainDisabledCode }
  /** 定制域名且当前站点智能体可用时，沿用最近一次访问（通常为创作页） */
  | { type: "resume-last-visit" };

export interface AgentEntryEnv {
  // 开发环境
  isDevEnv: boolean;
  // 测试主站
  isTestMainEnv: boolean;
  // 生产主域名
  isProdMainEnv: boolean;
  // 定制域名
  isCustomDomain: boolean;
}

/** 定制域名下「无法沿用最近访问」的原因：智能体侧停用 vs 当前用户账号停用 */
export enum CustomDomainDisabledCode {
  AGENT_DISABLED = "AGENT_DISABLED",
  ACCOUNT_DISABLED = "ACCOUNT_DISABLED",
}

/**
 * 定制域名：与 {@link resolveAgentEntryDestination} 中定制域名分支一致。
 * 若应展示「智能体/账号停用」说明则返回原因 code，否则 undefined（表示应沿用最近访问）。
 */
export function getCustomDomainDisabledCode(params: {
  agents: AgentData[];
  domainAgentId: string;
  domainAgentStatus: number | undefined;
}): CustomDomainDisabledCode | undefined {
  const { agents, domainAgentId, domainAgentStatus } = params;
  const domainAgent = agents.find((a) => a?.id === domainAgentId);

  // 解析的智能体在列表中存在
  if (domainAgent) {
    // 该智能体可用，沿用最近访问
    if (isAgentAvailableForUser(domainAgent)) {
      return undefined;
    }
    // 该智能体 status 被禁用，展示停用说明
    if (domainAgent.status !== 1) {
      return CustomDomainDisabledCode.AGENT_DISABLED;
    }
    // 走到这一步，说明 enableStatus 和 manageStatus 都被禁用了
    return CustomDomainDisabledCode.ACCOUNT_DISABLED;
  }

  // 解析的智能体在列表中不存在，但 status 为开启，沿用最近访问
  if (domainAgentStatus === 1) {
    return undefined;
  }

  // 解析的智能体在列表中不存在，且 status 被禁用，展示停用说明
  return CustomDomainDisabledCode.AGENT_DISABLED;
}

/**
 * 登录后首次编排：根据环境 + 列表可用性 + 当前域名解析智能体，决定入口去向。
 * 与选择系统页弹窗展示规则共用「可用」定义 {@link isAgentAvailableForUser}。
 * 开发环境 / 测试主站：有可用智能体时按域名解析智能体决定选系统或沿用最近访问；否则与全环境一致去选系统。
 */
export function resolveAgentEntryDestination(params: {
  env: AgentEntryEnv;
  agents: AgentData[];
  domainAgentId: string;
  domainAgentStatus: number | undefined;
}): AgentEntryDestination {
  const { env, agents, domainAgentId, domainAgentStatus } = params;
  const hasAnyAvailable = agents.some(isAgentAvailableForUser);

  // 非定制域名, 无任意可用智能体时去选系统
  if (!env.isCustomDomain && !hasAnyAvailable) {
    return { type: "select-system" };
  }

  // 生产主域名，直接前往选择系统页
  if (env.isProdMainEnv) {
    return { type: "select-system" };
  }

  // 定制域名
  if (env.isCustomDomain) {
    const disabledCode = getCustomDomainDisabledCode({
      agents,
      domainAgentId,
      domainAgentStatus,
    });
    if (disabledCode !== undefined) {
      return { type: "select-system" };
    }
    return { type: "resume-last-visit" };
  }

  // 开发环境 / 测试主站：解析的智能体在列表中可用则沿用最近一次访问，否则去选系统
  if (env.isDevEnv || env.isTestMainEnv) {
    const domainAgent = agents.find((a) => a.id === domainAgentId);
    if (domainAgent && isAgentAvailableForUser(domainAgent)) {
      return { type: "resume-last-visit" };
    }
    return { type: "select-system" };
  }

  return { type: "resume-last-visit" };
}
